import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../redux/type";
import { getUser, mineTokens } from "../api/user";
import { setUserData } from "../redux/actions";
import toast from "react-hot-toast";
import { formatNumber } from "../utils/helpers";
import ButtonTasks from "../components/Tasks/ButtonTasks";
import UserProfile from "../components/Tasks/UserProfile";
import { motion } from "framer-motion";
import { MarginBottom } from "../components/MarginBottom";
import MissionsSection from "../components/MissionsSection";
import { List } from "lucide-react";

// Reuse animations from Admin panel
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0, 224, 255, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(0, 224, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 224, 255, 0); }
`;

// Styled Components (same as before)
const Container = styled.div`
  max-width: 1200px;
  background: #121212;
  backdrop-filter: blur(10px);
  animation: ${fadeIn} 0.5s ease-out;
`;

const HelloText = styled.div`
  color: #fff;
  font-size: 1.2rem;
  font-weight: 700;
  padding: 0 1rem;
  text-align: center;
`;

const SectionTitle = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  text-align: center;
  background: linear-gradient(90deg, #ccff00, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  padding-bottom: 0.5rem;
  margin-top: 1.5rem;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #ccff00, #3b82f6);
    border-radius: 3px;
  }
`;

const TaskList = styled.div`
  padding: 1rem;
`;

const TaskTitle = styled.div`
  color: #ccff00;
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
  font-weight: 600;
  display: flex;
  gap: 0.5rem;
`;

const GamePage = () => {
  const user = useSelector((state: RootState) => state.userData);
  const dispatch = useDispatch();
  const tasks = useSelector((state: RootState) => state.tasks);
  const [miningInProgress, setMiningInProgress] = useState(false);
  const [timeUntilNextMine, setTimeUntilNextMine] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate remaining time until next mining is available
  useEffect(() => {
    if (user && user.createdAt && user.lifelineTime) {
      const { remainingTime } = calculateLifeTimeData();
      const secondsLeft = Math.max(Math.floor(remainingTime / 1000), 0);
      setTimeUntilNextMine(secondsLeft);
    }
  }, [user]);

  // Timer countdown effect
  useEffect(() => {
    if (timeUntilNextMine > 0) {
      timerRef.current = setTimeout(
        () => setTimeUntilNextMine(timeUntilNextMine - 1),
        1000
      ) as any;
    } else if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [timeUntilNextMine]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const calculateLifeTimeData = () => {
    const createdAt = new Date(user.createdAt);
    const lifelineTime = user.lifelineTime; // hours
    const lifelineTimeMs = lifelineTime * 3600 * 1000;

    const now = new Date();
    const remainingMs = lifelineTimeMs - (now.getTime() - createdAt.getTime());

    const remainingSeconds = Math.max(Math.floor(remainingMs / 1000), 0);

    return {
      remainingTime: remainingMs,
      formattedTime: formatTime(remainingSeconds),
    };
  };

  return (
    <>
      <Container>
        <UserProfile
          user={user}
          mini={true}
          remainingTime={timeUntilNextMine}
        />
        <HelloText> Welcome @{user.username}</HelloText>
        <MissionsSection />
        <TaskList>
          <TaskTitle>
            <List className="w-5 h-5 text-playops-accent" />
            Quests
          </TaskTitle>
          {tasks?.length === 0 ? (
            <div style={{ color: "rgba(255, 255, 255, 0.5)" }}>
              No quests available at the moment
            </div>
          ) : (
            <ButtonTasks mini limit={3} />
          )}
        </TaskList>
      </Container>
      <MarginBottom />
    </>
  );
};

export default GamePage;
